/**
 * Dependencies.
 */
import { RawLocation, Route, RouteConfig } from 'vue-router';

import { partnershipService } from '@/api/detail/partnership/partnership.service';
import {
  CampaignTypeEnum,
  ON_DEMAND_CAMPAIGN_TYPES
} from '@/api/on-demand-campaigns/campaign-for-partnership.model';
import { campaignForPartnershipService } from '@/api/on-demand-campaigns/campaign-for-partnership.service';
import { ROUTES } from '@/constants/routes.constant';

/**
 * Resolve the current partnership id: query string, then the store,
 * then the first partnership of the logged account (same order as the app scene).
 */
const resolvePartnershipId = async (to: Route): Promise<number | null> => {
  let partnershipId = Number(to.query.partnershipId) || null;

  if (!partnershipId) {
    const { data: partnerships = [] } =
      (await partnershipService.getPartnerships({ limit: 1, page: 0 })) || {};

    partnershipId = Number(partnerships?.[0]?.id) || null;
  }

  return partnershipId;
};

/**
 * Build a `?key=value` search string from a route query (empty when no params).
 */
const toSearch = (query: Route['query']): string => {
  const search = new URLSearchParams(
    query as Record<string, string>
  ).toString();

  return search ? `?${search}` : '';
};

/**
 * Export.
 */
export default [
  {

    /**
     * Resolve the campaign type and forward to the matching detail scene.
     */
    async beforeEnter(to, _from, next) {
      const id = Number(to.params.id);

      let target: RawLocation | false = false;
      let href: string | null = `/${toSearch(to.query)}`;

      try {
        const partnershipId = await resolvePartnershipId(to);

        if (!partnershipId) {
          throw new Error('Partnership not found.');
        }

        const query = { ...to.query, partnershipId: String(partnershipId) };
        const type = await campaignForPartnershipService.getCampaignType({
          id,
          partnershipId
        });

        if (type === CampaignTypeEnum.OFFER) {
          href = null;
          target = {
            name: ROUTES.CAMPAIGNS.CAMPAIGNS_DETAIL,
            params: { id: `${id}` },
            query
          };
        } else if (type === CampaignTypeEnum.GENERIC) {
          href = null;
          target = {
            name: ROUTES.CAMPAIGNS.GENERICS_DETAIL,
            params: { id: `${id}` },
            query
          };
        } else if (ON_DEMAND_CAMPAIGN_TYPES.includes(type)) {
          href = `/apps/on-demand-campaigns/detail/${id}${toSearch(query)}`;
        }
      } catch (error) {
        // Keep `/` as fallback.
      }

      if (href) {
        window.location.href = href;
      }

      next(target);
    },
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.CAMPAIGNS_RESOLVE,
    path: 'campaigns/:id(\\d+)/resolve'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/campaigns-list/campaigns-list.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.CAMPAIGNS_LIST,
    path: 'campaigns/offers'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/campaigns-detail/campaigns-detail.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.CAMPAIGNS_DETAIL,
    path: 'campaigns/offers/:id'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/generics-list/generics-list.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.GENERICS_LIST,
    path: 'campaigns/generics'
  },
  {
    component: () =>
      import(
        /* webpackChunkName: "campaigns" */ '@/modules/campaigns/scenes/generics-detail/generics-detail.scene.vue'
      ),
    meta: { requiresAuth: true },
    name: ROUTES.CAMPAIGNS.GENERICS_DETAIL,
    path: 'campaigns/generics/:id'
  }
] as RouteConfig[];
